/* ==========================================================================
   KOLP — Core runtime
   Tiny utilities, a pub/sub bus, the analytics dispatcher and the consent
   gate. No framework. Loaded in <head> with `defer`.
   ========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  var KOLP = window.KOLP || {};
  window.KOLP = KOLP;

  /* ---- settings injected by Liquid (snippet: theme-globals) ---------------- */
  KOLP.settings = window.__KOLP_SETTINGS__ || {};
  KOLP.routes = window.__KOLP_ROUTES__ || { cart_add_url: '/cart/add', cart_url: '/cart', cart_change_url: '/cart/change', cart_url_js: '/cart.js' };

  /* ---- DOM helpers -------------------------------------------------------- */
  KOLP.$ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  KOLP.$$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  KOLP.on = function (el, type, handler, opts) {
    (Array.isArray(el) ? el : [el]).forEach(function (n) { n && n.addEventListener(type, handler, opts); });
  };

  KOLP.debounce = function (fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait || 150);
    };
  };

  KOLP.trapFocus = function (container) {
    var focusable = KOLP.$$('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return function () {};
    var first = focusable[0], last = focusable[focusable.length - 1];
    function handle(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handle);
    return function () { container.removeEventListener('keydown', handle); };
  };

  /* ---- pub/sub ---------------------------------------------------------- */
  var listeners = {};
  KOLP.bus = {
    on: function (evt, cb) { (listeners[evt] = listeners[evt] || []).push(cb); },
    off: function (evt, cb) { listeners[evt] = (listeners[evt] || []).filter(function (f) { return f !== cb; }); },
    emit: function (evt, data) { (listeners[evt] || []).forEach(function (cb) { try { cb(data); } catch (e) { console.error(e); } }); }
  };

  /* ---- consent gate ---------------------------------------------------- */
  /* Uses Shopify's Customer Privacy API when present; otherwise a local
     fallback stored in localStorage. Marketing/analytics tags MUST await
     KOLP.consent.ready() and check the relevant grant. */
  var LS_KEY = 'kolp_consent_v1';
  var consentState = { analytics: false, marketing: false, preferences: false, determined: false };
  var consentCallbacks = [];

  function readLocalConsent() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }
  function writeLocalConsent(obj) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch (e) {}
  }

  function resolveConsent() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (api && typeof api.analyticsProcessingAllowed === 'function') {
      try {
        consentState.analytics = api.analyticsProcessingAllowed();
        consentState.marketing = api.marketingAllowed ? api.marketingAllowed() : consentState.analytics;
        consentState.preferences = api.preferencesProcessingAllowed ? api.preferencesProcessingAllowed() : false;
        consentState.determined = api.shouldShowBanner ? !api.shouldShowBanner() : true;
      } catch (e) {}
    } else {
      var local = readLocalConsent();
      if (local) {
        consentState.analytics = !!local.analytics;
        consentState.marketing = !!local.marketing;
        consentState.preferences = !!local.preferences;
        consentState.determined = true;
      }
    }
    consentCallbacks.forEach(function (cb) { try { cb(consentState); } catch (e) {} });
    KOLP.bus.emit('consent:change', consentState);
  }

  KOLP.consent = {
    state: consentState,
    ready: function (cb) {
      consentCallbacks.push(cb);
      if (consentState.determined) cb(consentState);
    },
    set: function (grants) {
      consentState.analytics = !!grants.analytics;
      consentState.marketing = !!grants.marketing;
      consentState.preferences = !!grants.preferences;
      consentState.determined = true;
      writeLocalConsent(consentState);
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.setTrackingConsent === 'function') {
        try {
          api.setTrackingConsent({
            analytics: consentState.analytics,
            marketing: consentState.marketing,
            preferences: consentState.preferences,
            sale_of_data: consentState.marketing
          }, function () {});
        } catch (e) {}
      }
      resolveConsent();
    },
    needsBanner: function () {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.shouldShowBanner === 'function') {
        try { return api.shouldShowBanner(); } catch (e) {}
      }
      return !readLocalConsent();
    }
  };

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && !consentState.determined) resolveConsent();
  });
  window.addEventListener('load', resolveConsent);
  // Shopify fires this when the privacy API is ready
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', resolveConsent);
  setTimeout(resolveConsent, 800);

  /* ---- analytics dispatcher ------------------------------------------- */
  /* One funnel. Providers subscribe. Events are de-duplicated per payload
     signature within a short window to avoid double-fires. */
  var recent = {};
  KOLP.analytics = {
    track: function (event, payload) {
      payload = payload || {};
      var sig = event + ':' + JSON.stringify(payload);
      var now = Date.now();
      if (recent[sig] && now - recent[sig] < 1200) return;
      recent[sig] = now;

      if (KOLP.settings.analyticsDebug) console.log('[KOLP analytics]', event, payload);

      // Always available: Shopify's own analytics / dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'kolp_' + event }, payload));

      KOLP.consent.ready(function (c) {
        if (!c.analytics && !c.marketing) return;
        KOLP.bus.emit('analytics:' + event, payload);
        KOLP.bus.emit('analytics:*', { event: event, payload: payload });
      });
    }
  };

  /* ---- money ---------------------------------------------------------- */
  KOLP.money = function (minor) {
    if (window.KOLPPricing) return window.KOLPPricing.formatMoney(minor);
    return (KOLP.settings.moneySymbol || '€') + ' ' + (Number(minor) / 100).toFixed(2);
  };

  /* Configure pricing from injected settings as soon as possible. */
  if (window.KOLPPricing && KOLP.settings.pricing) {
    window.KOLPPricing.configure(KOLP.settings.pricing);
  }

  KOLP.bus.emit('core:ready');
})();
